import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import SettingsAiVoicesLink from './SettingsAiVoicesLink';
import VoiceAuditionPanel from './VoiceAuditionPanel';
import VoiceInfoCard from './VoiceInfoCard';
import { EmptyState } from './voiceMapperStyles';

import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';

interface SpeakerVoiceAuditionAreaProps {
  /** All workspace-activated voices — the pool to browse, audition, and add from. */
  voices: AiVoiceRecord[];
  /** IDs of voices already on the speaker (checkmark + "On This Speaker" facet source). */
  memberIds: Set<string>;
  /** Add the voice to / remove it from the speaker. */
  onToggleMember: (voiceId: string) => void;
  busy?: boolean;
}

// The "Find & Audition Voices" tab of the speaker Manage modal. A speaker-scoped
// reuse of the Settings "Audition a voice" layout: browse ALL workspace voices,
// audition any with a custom phrase (PLAT-10503), hear per-language samples via
// the VoiceInfoCard (PLAT-10502), and add/remove the focused voice to/from the
// speaker. Every membership control speaks of the speaker, not the workspace —
// the panel's default wording belongs to Settings (PLAT-11577).
const SpeakerVoiceAuditionArea = observer(
  ({
    voices,
    memberIds,
    onToggleMember,
    busy,
  }: SpeakerVoiceAuditionAreaProps) => {
    const [focusedVoiceId, setFocusedVoiceId] = useState(voices[0]?.id ?? '');

    // Keep the focus valid as the pool changes underneath us.
    useEffect(() => {
      if (voices.length > 0 && !voices.some(v => v.id === focusedVoiceId)) {
        setFocusedVoiceId(voices[0].id);
      }
    }, [voices, focusedVoiceId]);

    if (voices.length === 0) {
      return (
        <EmptyState>
          No AI Voices are activated for this workspace yet. Activate some in{' '}
          <SettingsAiVoicesLink />, then add them to the speaker here.
        </EmptyState>
      );
    }

    const focusedVoice = voices.find(v => v.id === focusedVoiceId) ?? null;

    return (
      <Layout>
        <VoiceAuditionPanel
          voices={voices}
          voiceId={focusedVoiceId}
          onVoiceChange={setFocusedVoiceId}
          membership={{
            label: 'On This Speaker',
            ids: memberIds,
            options: { included: 'Added', excluded: 'Not Added' },
            checkLabel: 'On this speaker',
            addLabel: 'Add to Speaker',
            removeLabel: 'Remove from Speaker',
          }}
          activation={{
            isActivated: focusedVoice ? memberIds.has(focusedVoice.id) : false,
            onToggle: () => focusedVoice && onToggleMember(focusedVoice.id),
            busy,
          }}
        />
        {focusedVoice && (
          <CardCell>
            <VoiceInfoCard voice={focusedVoice} variant="detail" />
          </CardCell>
        )}
      </Layout>
    );
  },
);

export default SpeakerVoiceAuditionArea;

const Layout = styled.div`
  display: grid;
  /* Left (filters + audition) flexes; the voice pane holds a comfortable fixed
     width so its language rows read well without starving the filter chips.
     The pane is the quiet detail variant, as on the Voices tab, so the
     audition form stays the thing the eye lands on. */
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 24px;
  align-items: start;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const CardCell = styled.div`
  position: relative;
  min-height: 320px;

  @media (max-width: 720px) {
    position: static;
    min-height: 0;
  }
`;
